import type { Board } from "@gjwtrack/domain";
import { ChevronDown, Star } from "lucide-react";
import { useEffect, useState, type MouseEvent } from "react";
import { useNavigate } from "react-router";
import { client, useClient } from "../lib/client.ts";
import { userName } from "../lib/format.ts";
import { Select, SelectType, type SelectItem } from "../ui/ring.ts";

// A stable empty list: returning a fresh [] from the selector would re-render forever (useSyncExternalStore compares snapshots).
const NO_FAVORITES: number[] = [];

/** YouTrack's board switcher in the breadcrumb: board name ˅ opens a filterable list; each row shows the owner and a star (favourites sort first). */
export function BoardSwitcher({ current }: { current: Board }) {
  const navigate = useNavigate();
  const schema = useClient((s) => s.schema);
  const favorites = useClient((s) => s.me?.settings.favoriteBoards) ?? NO_FAVORITES;
  const [boards, setBoards] = useState<Board[]>([]);
  useEffect(() => {
    client.http.boards().then(setBoards).catch(() => {});
  }, []);
  const isFav = (id: number): boolean => favorites.includes(id);
  const toggleFav = (id: number): void => {
    void client.setUserSettings({ favoriteBoards: isFav(id) ? favorites.filter((f) => f !== id) : [...favorites, id] });
  };
  const stop = (e: MouseEvent): void => {
    e.stopPropagation();
    e.preventDefault();
  };
  const list = boards.length ? boards : [current];
  const data: SelectItem[] = [...list]
    .sort((a, b) => Number(isFav(b.id)) - Number(isFav(a.id)) || a.name.localeCompare(b.name))
    .map((b) => ({
      key: b.id,
      label: b.name,
      rightNodes: (
        <span className="flex items-center gap-2 pl-4 text-muted">
          {b.createdBy !== null && <span className="text-[13px]">{userName(schema, b.createdBy)}</span>}
          <button
            className={`flex h-5 w-5 items-center justify-center ${isFav(b.id) ? "text-star" : "text-faint hover:text-ink"}`}
            title={isFav(b.id) ? "Remove from favorites" : "Add to favorites"}
            onMouseDown={stop}
            onMouseUp={stop}
            onClick={(e) => {
              stop(e);
              toggleFav(b.id);
            }}
          >
            <Star size={15} fill={isFav(b.id) ? "currentColor" : "none"} />
          </button>
        </span>
      ),
    }));
  data.push({ key: "all", label: "All boards…" });
  return (
    <Select
      filter
      type={SelectType.CUSTOM}
      data={data}
      selected={data.find((d) => d.key === current.id) ?? null}
      minWidth={380}
      onSelect={(item) => {
        if (!item) return;
        if (item.key === "all") navigate("/agiles/all");
        else if (Number(item.key) !== current.id) navigate(`/agiles/${item.key}/current`);
      }}
      customAnchor={({ wrapperProps, buttonProps, popup }) => (
        <span {...wrapperProps} className="relative inline-flex">
          <button {...buttonProps} className="flex items-center gap-1 rounded px-1 text-[15px] hover:bg-sunken" title="Switch board">
            <span>{current.name}</span>
            <ChevronDown size={16} className="text-muted" />
          </button>
          {popup}
        </span>
      )}
    />
  );
}
